import type { PermissionRoleCreate, UserPermissions } from '@/api';
import { hasReviewArea } from '@/stores/auth';

/** A role's organisation-wide access levels — bitmasks, not on/off. */
export type RoleAccessField = 'all_contractual_parties_access' | 'all_contracts_access';

/** A role's on/off capabilities. */
export type RoleFlag = Exclude<
  keyof PermissionRoleCreate,
  'name' | 'description' | RoleAccessField
>;

export type RoleFlagEntry =
  | { kind: 'toggle'; key: RoleFlag; label: string; hint: string }
  | { kind: 'access'; key: RoleAccessField; label: string; hint: string };

export interface RoleFlagGroup {
  title: string;
  flags: RoleFlagEntry[];
}

/**
 * A role's capabilities, grouped by the feature they unlock, in the words the
 * role dialog and the role page both use. A group only appears when the
 * organisation has that feature. Signing is always offered: it can come from
 * the GAIA provider or a connected Youtrust integration, so it must not hide
 * behind the GAIA-only feature. Reviews, playbooks and review projects all
 * belong to contract review; only org admins see these dialogs, and their
 * elevated flags are true exactly when the organisation has that feature.
 */
export function roleFlagGroups(permissions: UserPermissions): RoleFlagGroup[] {
  const hasContractReview = hasReviewArea(permissions);
  const groups: (RoleFlagGroup & { visible: boolean })[] = [
    {
      title: 'Reviews',
      visible: hasContractReview,
      flags: [
        {
          kind: 'toggle',
          key: 'can_create_review',
          label: 'Create reviews',
          hint: 'Start new contract reviews.',
        },
        {
          kind: 'toggle',
          key: 'can_update_review',
          label: 'Update reviews',
          hint: 'Change reviews that already exist.',
        },
      ],
    },
    {
      title: 'Playbooks',
      visible: hasContractReview,
      flags: [
        {
          kind: 'toggle',
          key: 'can_create_playbooks',
          label: 'Create playbooks',
          hint: 'Build and edit playbooks for the organisation.',
        },
        {
          kind: 'toggle',
          key: 'can_use_playbooks',
          label: 'Use playbooks',
          hint: 'Run existing playbooks against documents.',
        },
      ],
    },
    {
      title: 'Review projects',
      visible: hasContractReview,
      flags: [
        {
          kind: 'toggle',
          key: 'can_use_review_projects',
          label: 'Use review projects',
          hint: 'Open and work in review projects.',
        },
      ],
    },
    {
      title: 'Signing',
      visible: true,
      flags: [
        {
          kind: 'toggle',
          key: 'can_use_signing',
          label: 'Use signing',
          hint: 'Prepare and send documents for signature.',
        },
      ],
    },
    {
      title: 'Contractual parties',
      visible: true,
      flags: [
        {
          kind: 'access',
          key: 'all_contractual_parties_access',
          label: 'Access to all contractual parties',
          hint: 'What this role can do with every party in the organisation, on top of what is shared. Set it on "Organisation Member" to apply it to everyone.',
        },
      ],
    },
    {
      title: 'Contracts',
      visible: permissions.can_use_contract_management,
      flags: [
        {
          kind: 'access',
          key: 'all_contracts_access',
          label: 'Access to all contracts',
          hint: 'What this role can do with every contract in the organisation, on top of what is shared. Set it on "Organisation Member" to apply it to everyone.',
        },
      ],
    },
  ];
  return groups.filter(group => group.visible).map(({ title, flags }) => ({ title, flags }));
}
